import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Calendar } from "../components/ui/calendar";
import { ClockTimeInput } from "../components/ClockTimePicker";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import {
  Bell,
  CalendarClock,
  CalendarDays,
  Plus,
  Search,
  Edit2,
  Trash2,
  Layers,
  Settings,
  Info,
  UserCircle2,
} from "lucide-react";
import { Tip } from "../components/ui/tooltip";
import {
  Modal,
  FormField,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { Switch } from "../components/ui/switch";
import { DataTable, type Column } from "../components/DataTable";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import { showToast } from "../components/ui/custom-toast";
import { validateRequired } from "../../utils/validators";
import {
  MODULE_OPTIONS,
  FREQUENCY_OPTIONS,
  FREQUENCY_LABEL,
  WEEKDAY_OPTIONS,
  WEEKDAY_LABEL,
  WEEKDAY_SHORT_LABEL,
  SCHEDULER_MODULE_NAME,
  formatEnumLabel,
  formatScheduleTime,
  formatDateTime,
  isWeekdayListValue,
  isDateListValue,
  type Module,
  type Frequency,
  type Weekday,
} from "../../stores/schedulersStore";
import {
  fetchSchedulerContentReviewList,
  saveSchedulerContentReview,
  deleteSchedulerContentReview,
  fetchSchedulerContentReviewOrganizations,
  type SchedulerContentReviewItem,
  type SchedulerContentReviewListPayload,
  type SchedulerContentReviewSavePayload,
  type SchedulerContentReviewOrgOption,
} from "../../api/frameworksApi";

/** UI keys sortable in the table, mapped to the API's `sortByColumn` values. */
const SORT_COLUMN_MAP: Record<string, string> = {
  org: "orgName",
  module: "moduleName",
  importAt: "runTime",
};

/* ── Small visual helpers ──────────────────────────────── */

function ModuleBadge({ moduleName }: { moduleName: string }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <Layers className="size-3.5 flex-shrink-0 text-[#64748b] dark:text-white/40" />
      <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80 truncate">
        {formatEnumLabel(moduleName)}
      </span>
    </div>
  );
}

function FrequencyBadge({ frequency }: { frequency: string }) {
  return (
    <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
      {FREQUENCY_LABEL[frequency as Frequency] ?? formatEnumLabel(frequency)}
    </span>
  );
}

/**
 * `runOn` holds different shapes depending on frequency:
 * - DAILY: empty or the literal "DAILY"
 * - WEEKLY: full weekday names (e.g. ["MONDAY", "FRIDAY"])
 * - MONTHLY: day-of-month numbers as strings (e.g. ["1", "15"])
 */
function ScheduleOnBadge({ frequency, runOn }: { frequency: string; runOn: string[] }) {
  const days = runOn ?? [];

  if (frequency === "WEEKLY" && days.length > 0) {
    const labels = days.map((d) => {
      const code = d.slice(0, 3).toUpperCase() as Weekday;
      return WEEKDAY_SHORT_LABEL[code] ?? formatEnumLabel(d);
    });
    return (
      <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
        <CalendarDays className="size-3.5 text-[#94a3b8] dark:text-white/40" />
        {labels.join(", ")}
      </span>
    );
  }

  if (frequency === "MONTHLY" && days.length > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
        <CalendarDays className="size-3.5 text-[#94a3b8] dark:text-white/40" />
        Day {days.join(", ")}
      </span>
    );
  }

  return (
    <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
      {FREQUENCY_LABEL[frequency as Frequency] ?? formatEnumLabel(frequency)}
    </span>
  );
}

function ScheduleTimeBadge({ time }: { time: string }) {
  return (
    <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
      {formatScheduleTime(time)}
    </span>
  );
}

function IconActionButton({
  icon,
  title,
  onClick,
  tone = "default",
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "text-[#94a3b8] dark:text-white/30 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
      : "text-[#475569] dark:text-white/50 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-md transition-colors ${toneClass}`}
    >
      {icon}
    </button>
  );
}

/* ── Alert Config ──────────────────────────────────────── */

/** Common fields collected by the Add/Edit form — module-specific criteria
 * is configured separately, from the Configuration page. */
type CommonDraft = {
  orgId: string;
  module: Module;
  frequency: Frequency;
  importOn: string;
  importAt: string;
};

const EMPTY_DRAFT: CommonDraft = {
  orgId: "",
  module: "CONTENT_REGISTRY" as Module,
  frequency: "DAILY" as Frequency,
  importOn: "DAILY",
  importAt: "10:00",
};
 
/** Builds the save API's `runOn` array from the Add/Edit draft — full weekday
 *  names for WEEKLY (e.g. "MONDAY"), day-of-month numbers for MONTHLY. */
function draftRunOnPayload(draft: CommonDraft): string[] {
  if (draft.frequency === "WEEKLY") {
    return draft.importOn
      ? draft.importOn
          .split(",")
          .filter(Boolean)
          .map((code) => (WEEKDAY_LABEL[code as Weekday] ?? code).toUpperCase())
      : [];
  }
  if (draft.frequency === "MONTHLY") {
    return draft.importOn
      ? draft.importOn
          .split(",")
          .filter(Boolean)
          .map((d) => String(Number(d.split("-")[2])))
      : [];
  }
  return [];
}
 
function AlertConfigTab() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<SchedulerContentReviewItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<SchedulerContentReviewItem | null>(null);
  const [draft, setDraft] = useState<CommonDraft>(EMPTY_DRAFT);
  const [formErrors, setFormErrors] = useState<{
    orgId?: string;
    module?: string;
    scheduleOn?: string;
    scheduleTime?: string;
  }>({});
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const [orgOptions, setOrgOptions] = useState<SchedulerContentReviewOrgOption[]>([]);
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOrgOptionsLoading(true);
    fetchSchedulerContentReviewOrganizations()
      .then((res) => {
        if (!cancelled) setOrgOptions(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setOrgOptions([]);
      })
      .finally(() => {
        if (!cancelled) setOrgOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SchedulerContentReviewItem | null>(null);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Debounce free-text search before it goes to the server.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const payload: SchedulerContentReviewListPayload = {};
      if (debouncedSearch) payload.keyword = debouncedSearch;
      if (sortKey) {
        payload.sortByColumn = SORT_COLUMN_MAP[sortKey] ?? sortKey;
        payload.sortingOrder = sortDir;
      }

      const res = await fetchSchedulerContentReviewList(page, pageSize, payload);
      if (res.status !== "success") {
        throw new Error("Failed to load schedules.");
      }
      setRows(res.data ?? []);
      setTotalItems(res.pagination?.totalCount ?? (res.data ?? []).length);
    } catch (err: any) {
      setLoadError(err?.message || "Failed to load schedules.");
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortKey, sortDir]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditTarget(null);
    setDraft(EMPTY_DRAFT);
    setFormErrors({});
    setShowForm(true);
  };

  const openEdit = (row: SchedulerContentReviewItem) => {
    setEditTarget(row);
    const importOn =
      row.frequency === "WEEKLY"
        ? row.runOn.map((d) => d.slice(0, 3).toUpperCase()).join(",")
        : row.frequency === "MONTHLY"
          ? ""
          : "DAILY";
    setDraft({
      orgId: row.orgId,
      module: (MODULE_OPTIONS.find((o) => o.value === row.moduleName)?.value ??
        "CONTENT_REGISTRY") as Module,
      frequency: (row.frequency as Frequency) ?? "DAILY",
      importOn,
      importAt: row.runTime,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const orgCheck = validateRequired(draft.orgId, "Organization");
    const selectedOrg = orgOptions.find((o) => o.orgId === draft.orgId);
    const orgConflict =
      orgCheck.valid && !editTarget && selectedOrg?.scheduleConfigured
        ? "This organization already has a schedule. Edit the existing schedule instead."
        : undefined;
    const moduleCheck = validateRequired(draft.module, "Module");
    const scheduleOnCheck =
      draft.frequency === "DAILY"
        ? { valid: true }
        : draft.frequency === "WEEKLY"
          ? isWeekdayListValue(draft.importOn)
            ? { valid: true }
            : { valid: false, error: "Please select at least one day." }
          : isDateListValue(draft.importOn)
            ? { valid: true }
            : { valid: false, error: "Please select at least one date." };
    const scheduleTimeCheck = validateRequired(draft.importAt, "Schedule time");

    const nextErrors: typeof formErrors = {};
    if (!orgCheck.valid) nextErrors.orgId = orgCheck.error;
    else if (orgConflict) nextErrors.orgId = orgConflict;
    if (!moduleCheck.valid) nextErrors.module = moduleCheck.error;
    if (!scheduleOnCheck.valid) nextErrors.scheduleOn = scheduleOnCheck.error;
    if (!scheduleTimeCheck.valid) nextErrors.scheduleTime = scheduleTimeCheck.error;

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload: SchedulerContentReviewSavePayload = {
        id: editTarget ? editTarget.id : null,
        orgId: draft.orgId,
        moduleName: SCHEDULER_MODULE_NAME,
        frequency: draft.frequency,
        runOn: draftRunOnPayload(draft),
        runTime: draft.importAt,
        enabled: editTarget ? editTarget.enabled : true,
      };
      const res = await saveSchedulerContentReview(payload);
      if (res.status !== "success" || !res.data) {
        throw new Error("Failed to save schedule.");
      }

      if (editTarget) {
        setRows((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
        showToast("success", "Schedule updated.");
      } else {
        setRows((prev) => [res.data, ...prev]);
        setTotalItems((t) => t + 1);
        showToast("success", "Schedule created.");
      }
      setShowForm(false);
    } catch (err: any) {
      showToast("error", err?.message || "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row: SchedulerContentReviewItem) => {
    setDeleteTarget(row);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteSchedulerContentReview(deleteTarget.id);
      if (res.status !== "success") {
        throw new Error("Failed to delete schedule.");
      }
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setTotalItems((t) => Math.max(0, t - 1));
      showToast("success", "Schedule deleted.");
      setShowDelete(false);
    } catch (err: any) {
      showToast("error", err?.message || "Failed to delete schedule.");
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = (row: SchedulerContentReviewItem) => {
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, enabled: !r.enabled } : r)),
    );
  };

  const [showDetails, setShowDetails] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState<SchedulerContentReviewItem | null>(null);
  const openDetails = (row: SchedulerContentReviewItem) => {
    setDetailsTarget(row);
    setShowDetails(true);
  };

  const columns: Column<SchedulerContentReviewItem>[] = [
    {
      key: "sno",
      header: "#",
      align: "center",
      render: (_r, idx) => (
        <span className="text-xs font-medium text-[#94a3b8] dark:text-white/40">
          {page * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "org",
      header: "Organization",
      align: "left",
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <UserCircle2 className="size-3.5 flex-shrink-0 text-[#64748b] dark:text-white/40" />
          <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80 truncate">
            {r.orgName}
          </span>
        </div>
      ),
    },
    {
      key: "module",
      header: "Module",
      align: "left",
      sortable: true,
      render: (r) => <ModuleBadge moduleName={r.moduleName} />,
    },
    {
      key: "frequency",
      header: "Schedule Frequency",
      align: "center",
      render: (r) => <FrequencyBadge frequency={r.frequency} />,
    },
    {
      key: "importOn",
      header: "Schedule On",
      align: "center",
      render: (r) => <ScheduleOnBadge frequency={r.frequency} runOn={r.runOn} />,
    },
    {
      key: "importAt",
      header: "Schedule Time",
      align: "center",
      sortable: true,
      render: (r) => <ScheduleTimeBadge time={r.runTime} />,
    },
    {
      key: "configuration",
      header: "Configuration",
      align: "center",
      render: (r) => (
        <IconActionButton
          icon={<Settings className="size-4" />}
          title="Open configuration"
          onClick={() => navigate(`/administration/schedulers/${r.id}/configuration`)}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center">
          <Switch checked={r.enabled} onCheckedChange={() => toggleActive(r)} />
        </div>
      ),
    },
    {
      key: "details",
      header: "Details",
      align: "center",
      render: (r) => (
        <IconActionButton
          icon={<Info className="size-4" />}
          title="View details"
          onClick={() => openDetails(r)}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <Tip label="Edit schedule" side="bottom">
            <button
              onClick={() => openEdit(r)}
              className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded transition-colors"
            >
              <Edit2 className="size-4 d hover:text-indigo-600" />
            </button>
          </Tip>
          <Tip label="Delete schedule" side="bottom">
            <button
              onClick={() => openDelete(r)}
              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
            >
              <Trash2 className="size-4 d hover:text-red-600" />
            </button>
          </Tip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-[#A78BFA]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Schedulers</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="size-4" />
            New Schedule
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
          <input
            type="text"
            placeholder="Search schedule"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
          />
        </div>

        <div className="ml-auto">
          <RowsPerCardSelect
            value={pageSize}
            onChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            label="Rows per page"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        error={loadError}
        onRetry={fetchRows}
        rowKey={(r) => r.id}
        rowClassName={() => "hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]"}
        emptyText={search.trim() ? "No schedules match your search." : "No schedules configured yet."}
        emptyIcon={<Bell className="size-4 text-[#64748b] dark:text-white/40" />}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
      />

      {/* Add / Edit modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editTarget ? "Edit Schedule" : "Add Schedule"}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={saving ? "Saving..." : "Save"}
            submitDisabled={saving}
          />
        }
      >
        <div className="space-y-3.5">
          <FormField label="Organization" required error={formErrors.orgId}>
            <FormSelect
              value={draft.orgId}
              onChange={(v) => {
                setDraft((d) => ({ ...d, orgId: v }));
                setFormErrors((e) => ({ ...e, orgId: undefined }));
              }}
              options={orgOptions.map((o) => ({
                value: o.orgId,
                label: o.orgName,
              }))}
              placeholder={orgOptionsLoading ? "Loading organizations..." : "Select organization"}
              searchable
            />
          </FormField>
          <FormField label="Module" required error={formErrors.module}>
            <FormSelect
              value={draft.module}
              onChange={(v) => {
                setDraft((d) => ({ ...d, module: v as Module }));
                setFormErrors((e) => ({ ...e, module: undefined }));
              }}
              options={MODULE_OPTIONS}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3.5">
            <FormField label="Schedule Frequency" required>
              <FormSelect
                value={draft.frequency}
                onChange={(v) => {
                  const newFrequency = v as Frequency;
                  setDraft((d) => ({
                    ...d,
                    frequency: newFrequency,
                    importOn:
                      newFrequency === "DAILY"
                        ? "DAILY"
                        : newFrequency === "WEEKLY"
                          ? isWeekdayListValue(d.importOn)
                            ? d.importOn
                            : ""
                          : isDateListValue(d.importOn)
                            ? d.importOn
                            : "",
                  }));
                }}
                options={FREQUENCY_OPTIONS}
              />
            </FormField>

            {draft.frequency === "DAILY" ? (
              <FormField label="Schedule On" required>
                <div className="px-3 py-2 rounded-lg bg-muted border border-border text-sm text-muted-foreground">
                  Daily (Auto-selected)
                </div>
              </FormField>
            ) : draft.frequency === "WEEKLY" ? (
              <FormField label="Schedule On" required error={formErrors.scheduleOn}>
                <MultiSelectDropdown
                  options={WEEKDAY_OPTIONS}
                  selected={draft.importOn ? draft.importOn.split(",").filter(Boolean) : []}
                  onChange={(values) => {
                    setDraft((d) => ({ ...d, importOn: values.join(",") }));
                    setFormErrors((e) => ({ ...e, scheduleOn: undefined }));
                  }}
                  placeholder="Select days"
                  selectedNoun="Day"
                  className={`w-full ${formErrors.scheduleOn ? "border-red-400 dark:border-red-500/50" : ""}`}
                />
              </FormField>
            ) : (
              <FormField label="Schedule On" required error={formErrors.scheduleOn}>
                <PopoverPrimitive.Root open={showCalendarModal} onOpenChange={setShowCalendarModal}>
                  <PopoverPrimitive.Trigger asChild>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                        formErrors.scheduleOn ? "border-red-400 dark:border-red-500/50" : "border-border"
                      }`}
                    >
                      <span
                        className={`truncate ${draft.importOn ? "text-[#0f172a] dark:text-white/85" : "text-muted-foreground"}`}
                      >
                        {draft.importOn
                          ? draft.importOn
                              .split(",")
                              .filter(Boolean)
                              .map((d) =>
                                new Date(d).toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }),
                              )
                              .join(", ")
                          : "Click to select dates"}
                      </span>
                      <CalendarClock className="size-4 flex-shrink-0 text-[#94a3b8]" />
                    </button>
                  </PopoverPrimitive.Trigger>

                  <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Content
                      align="start"
                      sideOffset={6}
                      className="z-[200] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                    >
                      <Calendar
                        mode="multiple"
                        selected={
                          draft.importOn
                            ? draft.importOn.split(",").filter(Boolean).map((d) => new Date(d))
                            : []
                        }
                        onSelect={(dates) => {
                          const sorted = (dates ?? [])
                            .slice()
                            .sort((a, b) => a.getTime() - b.getTime())
                            .map((d) => d.toISOString().split("T")[0]);
                          setDraft((d) => ({ ...d, importOn: sorted.join(",") }));
                          setFormErrors((e) => ({ ...e, scheduleOn: undefined }));
                        }}
                      />
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Portal>
                </PopoverPrimitive.Root>
              </FormField>
            )}

            <FormField label="Schedule Time" required error={formErrors.scheduleTime}>
              <ClockTimeInput
                value={draft.importAt}
                onChange={(v) => {
                  setDraft((d) => ({ ...d, importAt: v }));
                  setFormErrors((e) => ({ ...e, scheduleTime: undefined }));
                }}
              />
            </FormField>
          </div>
        </div>
      </Modal>

      {/* Details — audit metadata. Modal is already centered with a blurred backdrop. */}
      <Modal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        title="Alert Details"
        footer={
          <div className="flex w-full items-center justify-end">
            <button
              onClick={() => setShowDetails(false)}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {detailsTarget && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                Created By
              </p>
              <div className="flex items-center gap-1.5">
                <UserCircle2 className="size-3.5 text-[#64748b] dark:text-white/40" />
                <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
                  {detailsTarget.createdByName}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                Updated By
              </p>
              <div className="flex items-center gap-1.5">
                <UserCircle2 className="size-3.5 text-[#64748b] dark:text-white/40" />
                <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
                  {detailsTarget.updatedByName}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                Created At
              </p>
              <p className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
                {formatDateTime(detailsTarget.createdAt)}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                Updated At
              </p>
              <p className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
                {formatDateTime(detailsTarget.updatedAt)}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete alert?"
        footer={
          <div className="flex w-full items-center justify-end gap-3">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors shadow-md shadow-red-500/20 disabled:opacity-40"
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-[#475569] dark:text-white/60">
          This alert will stop notifying recipients and will be removed from the list. This
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────── */

export function Schedulers() {
  return (
    <div className="p-6">
      <AlertConfigTab />
    </div>
  );
}
